/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/**
 * API Client Service
 *
 * This service provides a unified interface for HTTP requests that can switch between
 * Axios (for regular app context) and fetchWithLuiUtils (for iframe context).
 *
 */
import axios from 'axios';
import getEnvVar from '@/utilities/environment-variable';
import { fetchWithLuiUtils, getPortalUtilities } from './lui-portal-utilities-manager';
import sessionStorage, { STORAGE_KEY_AUTH_DATA } from './sessionStorage';

/**
 * Determines whether to use LUI utils or Axios based on current context
 * @returns {boolean} true if should use LUI utils, false for Axios
 */
const shouldUseLuiUtils = () => {
  // Check if we're in an iframe context and LUI utilities are available
  if (typeof window !== 'undefined' && getEnvVar('NEXT_PUBLIC_APIM_ROUTES_ENABLED') === 'true') {
    return getPortalUtilities()?.isIFrame();
  }
  return false;
};

/**
 * Transforms parameters for Axios requests
 * Axios has different signatures for different HTTP methods:
 * - GET: axios.get(url, config) where query params are in config.params
 * - POST/PUT/PATCH/DELETE: axios.method(url, data, config) where data is the request body
 */
const transformParamsForAxios = (method, url, params = {}, config = {}) => {
  const upperMethod = method.toUpperCase();

  if (upperMethod === 'GET' || upperMethod === 'DELETE') {
    return [url, { ...config, ...params }];
  }

  return [url, params, config];
};

/**
 * Makes an HTTP request using the appropriate client (Axios or LUI Utils)
 */
const makeRequest = async (method, url, params = {}, axiosConfig = {}) => {
  const shouldUseUtils = shouldUseLuiUtils();

  if (shouldUseUtils) {
    // Use LUI Utils for iframe context
    const apimPath = getEnvVar('NEXT_PUBLIC_DODDLE_APIM_PATH');
    const apiPath = getEnvVar('NEXT_PUBLIC_DODDLE_API_PATH');
    if (!apimPath || !apiPath) {
      throw new Error(
        'APIM routes are enabled, but NEXT_PUBLIC_DODDLE_APIM_PATH or NEXT_PUBLIC_DODDLE_API_PATH is not defined',
      );
    }
    let APIMUrl = `${apimPath}/${apiPath}/${url}`;
    if (axiosConfig.useReturnPortalService === false) {
      APIMUrl = url;
    }
    return fetchWithLuiUtils({
      url: APIMUrl,
      params,
      method: method.toUpperCase(),
    }).then((res) => ({ data: res })).catch((err) => { throw err; });
  }

  // Use Axios for regular context
  const apiUrl = getEnvVar('REACT_APP_DODDLE_API_URL');
  if (!apiUrl) {
    throw new Error('Host API URL is not defined in environment variables');
  }

  let fullUrl;
  // When customUrl flag is set, use the URL as-is without prefixing it
  if (axiosConfig.customUrl) {
    fullUrl = `${apiUrl}/${url}`;
  } else {
    fullUrl = `${apiUrl}/v1/returns-portal-service/${url}`;
  }

  const sessionAuthData = sessionStorage.getData(STORAGE_KEY_AUTH_DATA) || {};
  const hasSessionAuth = sessionAuthData.token_type && sessionAuthData.access_token;

  let finalConfig = axiosConfig;
  const authHeadersPassedIn = axiosConfig.headers?.Authorization || params.headers?.Authorization;
  const shouldApplySessionAuthHeaders = axiosConfig.headersOverride !== false;

  // pre-fill Authorization header from session storage if not already set
  if (hasSessionAuth && !authHeadersPassedIn && shouldApplySessionAuthHeaders) {
    finalConfig = {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        Authorization: `${sessionAuthData.token_type} ${sessionAuthData.access_token}`,
      },
    };
  }

  const axiosArgs = transformParamsForAxios(method, fullUrl, params, finalConfig);
  return axios[method.toLowerCase()](...axiosArgs);
};

/**
 * API Client object with HTTP method implementations
 */
const apiClient = {
  /**
   * Performs a GET request
   * @param {string} url - The URL to request
   * @param {Object} params - Query parameters
   * @param {Object} config - Additional Axios configuration
   * @returns {Promise} Response from the API
   */
  get: async (url, params = {}, config = {}) => makeRequest('GET', url, params, config),

  /**
   * Performs a POST request
   * @param {string} url - The URL to request
   * @param {Object} data - Request payload
   * @param {Object} config - Additional Axios configuration
   * @returns {Promise} Response from the API
   */
  post: async (url, data = {}, config = {}) => makeRequest('POST', url, data, config),

  /**
   * Performs a PUT request
   * @param {string} url - The URL to request
   * @param {Object} data - Request payload
   * @param {Object} config - Additional Axios configuration
   * @returns {Promise} Response from the API
   */
  put: async (url, data = {}, config = {}) => makeRequest('PUT', url, data, config),

  /**
   * Performs a DELETE request
   * @param {string} url - The URL to request
   * @param {Object} data - Request payload (optional)
   * @param {Object} config - Additional Axios configuration
   * @returns {Promise} Response from the API
   */
  delete: async (url, data = {}, config = {}) => makeRequest('DELETE', url, data, config),

  /**
   * Performs a PATCH request
   * @param {string} url - The URL to request
   * @param {Object} data - Request payload
   * @param {Object} config - Additional Axios configuration
   * @returns {Promise} Response from the API
   */
  patch: async (url, data = {}, config = {}) => makeRequest('PATCH', url, data, config),
};

export default apiClient;
